/**
 * Count-bounded quest runner. Spawns one worker agent per card sequentially
 * and feeds each the quest prompt (or the card's prompt override, when
 * `perCardPrompt: "free"`). After all cards finish, optionally runs a summarizer
 * card whose input is the concatenated final text of all preceding cards.
 *
 * v1 simplifications (intentional):
 *   - Role files are resolved at spawn time — the role body is passed as
 *     `systemPrompt` on the AgentSessionConfig.
 *   - Per-card model overrides are honored; per-card provider overrides are
 *     not (everything uses quest.defaultProvider).
 *   - No streaming-log capture into the quest record — the agent's own
 *     stream is already broadcast over WS via AgentManager subscribers, so
 *     clients can subscribe to `card.agentId` directly to see tool calls.
 *
 * Abort: respects the AbortSignal. Cards mid-flight are stopped via
 * AgentManager.cancelAgentRun; pending cards are marked stopped without spawning.
 */

import type { Logger } from "pino";
import type { AgentSessionConfig } from "../agent/agent-sdk-types.js";
import { getProviderFullAccessModeId } from "../agent/provider-manifest.js";
import { resolveRole } from "../roles/scanner.js";
import type { QuestRunner, QuestService } from "./service.js";
import type { QuestRecord, QuestCard } from "./types.js";
import { buildAgentOwnerOptions } from "./agent-owner-options.js";

function nowIso(): string {
  return new Date().toISOString();
}

async function buildAgentConfig(
  record: QuestRecord,
  card: QuestCard,
  titleSuffix: string,
): Promise<AgentSessionConfig> {
  const modeId = getProviderFullAccessModeId(record.defaultProvider) ?? undefined;
  let systemPrompt: string | undefined;
  let allowedTools: string[] | undefined;
  if (card.role) {
    const role = await resolveRole({ roleName: card.role, workspaceRoot: record.cwd });
    if (role) {
      if (role.body) systemPrompt = role.body;
      if (role.allowedTools.length > 0) allowedTools = role.allowedTools;
    }
  }
  return {
    provider: record.defaultProvider,
    cwd: record.cwd,
    model: card.model ?? record.defaultModel ?? undefined,
    modeId,
    systemPrompt,
    allowedTools,
    title: `quest ${record.id} ${titleSuffix}`,
    internal: true,
  };
}

function resolveFinalTextFromRun(run: {
  timeline: ReadonlyArray<unknown>;
  finalText: string;
}): string {
  if (run.finalText.trim()) {
    return run.finalText;
  }
  for (let i = run.timeline.length - 1; i >= 0; i -= 1) {
    const item = run.timeline[i];
    if (
      item &&
      typeof item === "object" &&
      "type" in item &&
      (item as { type: string }).type === "assistant_message" &&
      "text" in item &&
      typeof (item as { text: string }).text === "string"
    ) {
      const text = (item as { text: string }).text;
      if (text.trim()) {
        return text;
      }
    }
  }
  return "";
}

interface CardRunResult {
  status: "succeeded" | "failed" | "stopped";
  finalText: string;
}

export interface LoopRunnerOptions {
  logger: Logger;
}

export class LoopRunner implements QuestRunner {
  private readonly logger: Logger;

  constructor(options: LoopRunnerOptions) {
    this.logger = options.logger.child({ module: "quest-runner-loop" });
  }

  async execute(args: {
    questId: string;
    signal: AbortSignal;
    service: QuestService;
  }): Promise<void> {
    const { questId, signal, service } = args;
    const initial = service.getRecord(questId);
    if (!initial) {
      throw new Error(`Quest "${questId}" not found`);
    }
    if (initial.kind !== "loop" && initial.kind !== "run") {
      throw new Error(`LoopRunner cannot execute quest of kind "${initial.kind}"`);
    }

    const completedFinalTexts: string[] = [];
    let aborted = false;

    for (const card of initial.cards) {
      const current = service.getRecord(questId);
      if (!current || current.status !== "running") {
        aborted = true;
        break;
      }
      if (signal.aborted) {
        aborted = true;
        break;
      }

      const cardPrompt = card.prompt ?? initial.prompt;
      const result = await this.runCard({
        record: current,
        card,
        prompt: cardPrompt,
        titleSuffix: `card ${card.index}`,
        service,
        signal,
      });
      if (result.status === "succeeded") {
        completedFinalTexts.push(result.finalText);
      }
      if (result.status === "stopped") {
        aborted = true;
        break;
      }
      if (result.status === "failed") {
        await service.markStatus(questId, "failed");
        return;
      }
    }

    if (aborted) {
      // Mark any still-queued cards as stopped.
      const current = service.getRecord(questId);
      if (current) {
        for (const card of current.cards) {
          if (card.status === "queued" || card.status === "running") {
            await service.updateCard(questId, card.index, {
              status: "stopped",
              completedAt: nowIso(),
            });
          }
        }
        if (current.summarizerCard && current.summarizerCard.status === "queued") {
          await service.updateSummarizer(questId, {
            status: "stopped",
            completedAt: nowIso(),
          });
        }
      }
      await service.markStatus(questId, "stopped");
      return;
    }

    // Summarizer (optional)
    const beforeSummary = service.getRecord(questId);
    if (beforeSummary?.summarizerCard) {
      const summaryPrompt = buildSummaryPrompt(initial.prompt, completedFinalTexts);
      const summaryResult = await this.runSummarizer({
        record: beforeSummary,
        prompt: summaryPrompt,
        service,
        signal,
      });
      if (summaryResult.status === "failed") {
        await service.markStatus(questId, "failed");
        return;
      }
      if (summaryResult.status === "stopped") {
        await service.markStatus(questId, "stopped");
        return;
      }
    }

    await service.markStatus(questId, "succeeded");
  }

  private async runCard(args: {
    record: QuestRecord;
    card: QuestCard;
    prompt: string;
    titleSuffix: string;
    service: QuestService;
    signal: AbortSignal;
  }): Promise<CardRunResult> {
    const { record, card, prompt, titleSuffix, service, signal } = args;
    const startedAt = nowIso();
    const agent = await service.agentManager.createAgent(
      await buildAgentConfig(record, card, titleSuffix),
      undefined,
      buildAgentOwnerOptions(record),
    );
    await service.updateCard(record.id, card.index, {
      status: "running",
      agentId: agent.id,
      startedAt,
    });

    const onAbort = () => {
      void service.agentManager.cancelAgentRun(agent.id).catch(() => {});
    };
    signal.addEventListener("abort", onAbort, { once: true });

    try {
      const run = await service.agentManager.runAgent(agent.id, prompt);
      const finalText = resolveFinalTextFromRun(run);
      if (run.canceled) {
        await service.updateCard(record.id, card.index, {
          status: "stopped",
          completedAt: nowIso(),
        });
        return { status: "stopped", finalText };
      }
      await service.updateCard(record.id, card.index, {
        status: "succeeded",
        completedAt: nowIso(),
      });
      return { status: "succeeded", finalText };
    } catch (err) {
      this.logger.error(
        { err, questId: record.id, cardIndex: card.index },
        "loop-runner: card failed",
      );
      await service.updateCard(record.id, card.index, {
        status: "failed",
        completedAt: nowIso(),
      });
      return { status: "failed", finalText: "" };
    } finally {
      signal.removeEventListener("abort", onAbort);
      try {
        await service.agentManager.closeAgent(agent.id);
      } catch {
        // ignore cleanup errors
      }
    }
  }

  private async runSummarizer(args: {
    record: QuestRecord;
    prompt: string;
    service: QuestService;
    signal: AbortSignal;
  }): Promise<CardRunResult> {
    const { record, prompt, service, signal } = args;
    const summarizer = record.summarizerCard;
    if (!summarizer) {
      return { status: "succeeded", finalText: "" };
    }
    const startedAt = nowIso();
    const agent = await service.agentManager.createAgent(
      await buildAgentConfig(record, summarizer, "summary"),
      undefined,
      buildAgentOwnerOptions(record),
    );
    await service.updateSummarizer(record.id, {
      status: "running",
      agentId: agent.id,
      startedAt,
    });
    const onAbort = () => {
      void service.agentManager.cancelAgentRun(agent.id).catch(() => {});
    };
    signal.addEventListener("abort", onAbort, { once: true });
    try {
      const run = await service.agentManager.runAgent(agent.id, prompt);
      const finalText = resolveFinalTextFromRun(run);
      if (run.canceled) {
        await service.updateSummarizer(record.id, {
          status: "stopped",
          completedAt: nowIso(),
        });
        return { status: "stopped", finalText };
      }
      await service.updateSummarizer(record.id, {
        status: "succeeded",
        completedAt: nowIso(),
      });
      return { status: "succeeded", finalText };
    } catch (err) {
      this.logger.error({ err, questId: record.id }, "loop-runner: summarizer failed");
      await service.updateSummarizer(record.id, {
        status: "failed",
        completedAt: nowIso(),
      });
      return { status: "failed", finalText: "" };
    } finally {
      signal.removeEventListener("abort", onAbort);
      try {
        await service.agentManager.closeAgent(agent.id);
      } catch {
        // ignore cleanup errors
      }
    }
  }
}

function buildSummaryPrompt(taskPrompt: string, priorOutputs: string[]): string {
  const sections = priorOutputs
    .map((text, i) => `## Card ${i + 1} output\n\n${text || "(no output)"}`)
    .join("\n\n");
  return [
    "Summarise in 2 plain english paragraphs that are easily understandable for the user and not technical giberish.",
    "",
    "Original task:",
    "",
    taskPrompt,
    "",
    sections,
  ].join("\n");
}
